import { OwcCheckboxCustomEvent } from '@one/web-components/dist/types/components';
import { useCallback, useMemo } from 'react';

interface UsePreserveSelectedHeaderCheckPropsParams<T> {
    selectedRows: T[];
    data: T[] | undefined;
    setSelectedRows: React.Dispatch<React.SetStateAction<T[]>>;
}

export const usePreserveSelectedHeaderCheckProps = <T extends { id: string }>({
    selectedRows,
    data,
    setSelectedRows,
}: UsePreserveSelectedHeaderCheckPropsParams<T>) => {
    const selectedRowIds = useMemo(
        () => new Set(selectedRows?.map((selectedRow) => selectedRow.id) ?? []),
        [selectedRows]
    );

    const allRowsAreSelected = useMemo(() => {
        if (!data?.length) {
            return false;
        }
        if (selectedRowIds.size < data.length) {
            return false;
        }

        for (const dv of data) {
            if (!selectedRowIds.has(dv.id)) {
                return false;
            }
        }

        return true;
    }, [data, selectedRowIds]);

    const someRowsAreSelected = useMemo(() => {
        if (!data?.length || !selectedRowIds.size) {
            return false;
        }

        for (const dv of data) {
            if (selectedRowIds.has(dv.id)) {
                return true;
            }
        }

        return false;
    }, [data, selectedRowIds]);

    const handleHeaderCheckboxValueChange = useCallback(
        (ev: OwcCheckboxCustomEvent<boolean>) => {
            ev.preventDefault();

            if (!data?.length) {
                return;
            }

            setSelectedRows((prevSelectedRows) => {
                const deviceIds = new Set(data.map((tp) => tp.id));

                const someSelected = prevSelectedRows.some((row) => deviceIds.has(row.id));

                if (someSelected) {
                    return prevSelectedRows.filter((row) => !deviceIds.has(row.id));
                }
                return [...new Map([...prevSelectedRows, ...data].map((row) => [row.id, row])).values()];
            });
        },
        [data, setSelectedRows]
    );

    const headerCheckboxProps = useMemo(
        () => ({
            checked: allRowsAreSelected,
            indeterminate: !allRowsAreSelected && someRowsAreSelected,
            onValueChange: (ev: OwcCheckboxCustomEvent<boolean>) => handleHeaderCheckboxValueChange(ev),
        }),
        [selectedRows, data]
    );

    return { selectedRowIds, allRowsAreSelected, someRowsAreSelected, headerCheckboxProps };
};
